import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  FaTachometerAlt, FaUserPlus, FaSignOutAlt,
  FaBars, FaTimes, FaGem, FaUserCircle, FaChevronRight,
} from "react-icons/fa";

const adminMenu = [
  { name: "Dashboard",       path: "/dashboard", icon: <FaTachometerAlt /> },
  { name: "User Management", path: "/adduser",   icon: <FaUserPlus /> },
];
const shopMenu = [
  { name: "Add Gold Rate", path: "/addrate",  icon: <FaGem /> },
  { name: "Preview",       path: "/preview", icon: <FaTachometerAlt /> },
];

const Layout = ({ children }) => {
  const [role, setRole]           = useState("");
  const [currUser, setCurrUser]   = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogout, setShowLogout]   = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setCurrUser(user);
    setRole(localStorage.getItem("type") || "");
  }, []);

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const menuItems = role === "admin" ? adminMenu : shopMenu;

  const confirmLogout = () => {
    localStorage.clear();
    setShowLogout(false);
    navigate("/login");
  };

  const getPageTitle = () => {
    const m = [...adminMenu, ...shopMenu].find(i => i.path === location.pathname);
    return m ? m.name : "Dashboard";
  };

  return (
    <div className="adm-layout">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

        :root {
          --sb-bg:      #0B2E33;
          --sb-hover:   #4F7C82;
          --sb-active:  #931B5B;
          --sb-text:    #B8E3E9;
          --sb-muted:   rgba(184,227,233,0.45);
          --sb-border:  rgba(184,227,233,0.12);
          --c-bg:       #F2F2F2;
          --c-border:   #CBCBCB;
          --c-white:    #ffffff;
          --c-primary:  #0B2E33;
          --c-accent:   #931B5B;
          --c-teal:     #4F7C82;
          --c-text:     #1a1a1a;
          --c-muted:    #6b7280;
          --sidebar-w:  252px;
          --header-h:   64px;
        }

        *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
        body, html, #root { height:100%; font-family:'Inter',sans-serif; background:var(--c-bg); }
        ::-webkit-scrollbar       { width:5px; }
        ::-webkit-scrollbar-track { background:var(--c-bg); }
        ::-webkit-scrollbar-thumb { background:var(--c-border); border-radius:10px; }

        .adm-layout { display:flex; height:100vh; overflow:hidden; font-family:'Inter',sans-serif; }

        /* Overlay */
        .adm-overlay { display:none; position:fixed; inset:0; background:rgba(0,0,0,.5); z-index:200; }
        .adm-overlay.on { display:block; }

        /* ── Sidebar ── */
        .adm-sidebar {
          width:var(--sidebar-w); min-width:var(--sidebar-w);
          background:var(--sb-bg);
          display:flex; flex-direction:column;
          position:relative; z-index:300;
          transition:transform .3s cubic-bezier(.4,0,.2,1);
          box-shadow:3px 0 20px rgba(0,0,0,.2);
          overflow:hidden;
        }
        .adm-sidebar::before {
          content:''; position:absolute; top:-60px; right:-60px;
          width:200px; height:200px; border-radius:50%;
          background:radial-gradient(circle,rgba(184,227,233,.06) 0%,transparent 70%);
          pointer-events:none;
        }
        .adm-sidebar::after {
          content:''; position:absolute; bottom:-40px; left:-40px;
          width:160px; height:160px; border-radius:50%;
          background:radial-gradient(circle,rgba(147,27,91,.08) 0%,transparent 70%);
          pointer-events:none;
        }

        /* Brand */
        .adm-brand {
          display:flex; align-items:center; gap:12px;
          padding:26px 22px 20px;
          border-bottom:1px solid var(--sb-border); flex-shrink:0;
        }
        .adm-brand-icon {
          width:42px; height:42px;
          background:linear-gradient(135deg,#931B5B,#b0226f);
          border-radius:12px;
          display:flex; align-items:center; justify-content:center;
          font-size:19px;
          box-shadow:0 4px 14px rgba(147,27,91,.45); flex-shrink:0;
        }
        .adm-brand-name { font-size:18px; font-weight:800; color:var(--sb-text); letter-spacing:1.5px; }
        .adm-brand-sub  { font-size:9.5px; color:var(--sb-muted); letter-spacing:2px; text-transform:uppercase; margin-top:2px; }

        /* Nav */
        .adm-nav { flex:1; overflow-y:auto; padding:16px 10px; }
        .adm-nav::-webkit-scrollbar { width:3px; }
        .adm-nav::-webkit-scrollbar-thumb { background:rgba(184,227,233,.15); }
        .adm-nav-lbl {
          font-size:9px; font-weight:700; color:var(--sb-muted);
          letter-spacing:2.5px; text-transform:uppercase;
          padding:0 12px 8px; margin-top:4px;
        }
        .adm-nav-item {
          display:flex; align-items:center; gap:12px;
          padding:12px 15px; border-radius:11px;
          text-decoration:none; color:var(--sb-text);
          font-size:13.5px; font-weight:500;
          transition:all .22s ease; margin-bottom:3px;
          cursor:pointer; border:none; background:none;
          width:100%; text-align:left;
        }
        .adm-nav-item:hover {
          background:rgba(79,124,130,.28);
          color:#fff; transform:translateX(3px);
        }
        .adm-nav-item.active {
          background:linear-gradient(90deg,rgba(147,27,91,.35),rgba(147,27,91,.15));
          color:#fff; font-weight:700;
          box-shadow:inset 3px 0 0 #931B5B;
          border:1px solid rgba(147,27,91,.25);
        }
        .adm-nav-ico  { font-size:15px; width:18px; display:flex; align-items:center; justify-content:center; flex-shrink:0; color:var(--sb-text); }
        .adm-nav-chev { margin-left:auto; font-size:9px; opacity:0; transition:opacity .2s; }
        .adm-nav-item:hover .adm-nav-chev,
        .adm-nav-item.active .adm-nav-chev { opacity:1; }

        /* Sidebar footer */
        .adm-sidebar-ftr { padding:14px 10px 20px; border-top:1px solid var(--sb-border); flex-shrink:0; }
        .adm-logout-btn {
          display:flex; align-items:center; gap:12px;
          width:100%; padding:11px 15px;
          background:rgba(147,27,91,.15); border:1px solid rgba(147,27,91,.3);
          border-radius:11px; color:#f9a8d4;
          font-size:13.5px; font-weight:500; cursor:pointer;
          transition:all .22s ease; text-align:left;
        }
        .adm-logout-btn:hover { background:rgba(147,27,91,.35); color:#fff; border-color:rgba(147,27,91,.55); transform:translateX(2px); }

        /* ── Main ── */
        .adm-main { flex:1; display:flex; flex-direction:column; overflow:hidden; min-width:0; }

        /* ── Header ── */
        .adm-header {
          height:var(--header-h); min-height:var(--header-h);
          background:var(--c-white);
          border-bottom:1.5px solid var(--c-border);
          display:flex; align-items:center; padding:0 28px; gap:14px;
          box-shadow:0 2px 8px rgba(0,0,0,.05); z-index:100; flex-shrink:0;
        }
        .adm-hamburger {
          display:none; align-items:center; justify-content:center;
          width:36px; height:36px;
          background:var(--c-bg); border:1.5px solid var(--c-border);
          border-radius:9px; font-size:16px; color:var(--c-primary);
          cursor:pointer; transition:background .2s; flex-shrink:0;
        }
        .adm-hamburger:hover { background:#d9ecee; }

        .adm-hdr-title { flex:1; min-width:0; }
        .adm-hdr-page  { font-size:17px; font-weight:700; color:var(--c-primary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .adm-hdr-sub   { font-size:11px; color:var(--c-muted); margin-top:1px; }

        .adm-user-chip {
          display:flex; align-items:center; gap:9px;
          background:var(--sb-bg); border-radius:11px;
          padding:7px 14px 7px 8px; cursor:default; flex-shrink:0;
        }
        .adm-user-avatar {
          width:28px; height:28px; background:rgba(184,227,233,.2);
          border-radius:8px; display:flex; align-items:center; justify-content:center;
          font-size:15px; color:var(--sb-text);
        }
        .adm-user-name { font-size:12.5px; font-weight:600; color:var(--sb-text); max-width:120px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

        /* ── Content ── */
        .adm-content { flex:1; overflow-y:auto; padding:26px 28px; background:var(--c-bg); min-height:0; }

        /* ── Footer ── */
        .adm-footer {
          background:var(--c-white); border-top:1.5px solid var(--c-border);
          padding:10px 28px; display:flex; align-items:center; justify-content:space-between; flex-shrink:0;
        }
        .adm-footer-l { font-size:12px; color:var(--c-muted); }
        .adm-footer-l span { color:var(--c-primary); font-weight:700; }
        .adm-footer-r { font-size:11.5px; color:var(--c-border); }

        /* ── Logout Modal ── */
        .adm-moverlay {
          position:fixed; inset:0; background:rgba(0,0,0,.5);
          display:flex; align-items:center; justify-content:center; z-index:1500;
          backdrop-filter:blur(3px); animation:adm-fi .2s ease;
        }
        .adm-mbox {
          background:#fff; border-radius:18px; padding:36px 30px;
          width:90%; max-width:360px; text-align:center;
          box-shadow:0 20px 50px rgba(0,0,0,.2); animation:adm-su .25s ease;
        }
        .adm-mico   { font-size:44px; margin-bottom:14px; }
        .adm-mtitle { font-size:19px; font-weight:700; color:var(--c-primary); margin-bottom:8px; }
        .adm-msub   { font-size:13px; color:var(--c-muted); line-height:1.5; }
        .adm-mbtns  { display:flex; gap:12px; margin-top:26px; }
        .adm-mcancel {
          flex:1; padding:11px; background:var(--c-bg);
          border:1.5px solid var(--c-border); border-radius:11px;
          font-size:13.5px; font-weight:600; color:var(--c-primary); cursor:pointer;
        }
        .adm-mcancel:hover { background:#d9ecee; }
        .adm-mconfirm {
          flex:1; padding:11px; background:var(--c-accent); border:none;
          border-radius:11px; font-size:13.5px; font-weight:600; color:#fff; cursor:pointer;
          box-shadow:0 4px 12px rgba(147,27,91,.35); transition:opacity .2s;
        }
        .adm-mconfirm:hover { opacity:.88; }

        @keyframes adm-fi { from{opacity:0} to{opacity:1} }
        @keyframes adm-su { from{transform:translateY(18px);opacity:0} to{transform:translateY(0);opacity:1} }

        /* ── Responsive ── */
        @media(max-width:900px){
          .adm-sidebar { position:fixed; top:0; left:0; height:100vh; transform:translateX(-100%); }
          .adm-sidebar.open { transform:translateX(0); }
          .adm-hamburger { display:flex; }
        }
        @media(max-width:580px){
          .adm-content { padding:14px; }
          .adm-header  { padding:0 14px; }
          .adm-footer  { padding:8px 14px; flex-direction:column; gap:3px; text-align:center; }
          .adm-user-name { display:none; }
          .adm-user-chip { padding:7px; }
        }
      `}</style>

      <div className={`adm-overlay ${sidebarOpen ? "on" : ""}`} onClick={() => setSidebarOpen(false)} />

      {/* Sidebar */}
      <aside className={`adm-sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="adm-brand">
          <div className="adm-brand-icon">💎</div>
          <div>
            <div className="adm-brand-name">BOAS</div>
            <div className="adm-brand-sub"></div>
          </div>
        </div>

        <nav className="adm-nav">
          <div className="adm-nav-lbl">Main Menu</div>
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `adm-nav-item ${isActive ? "active" : ""}`}
            >
              <span className="adm-nav-ico">{item.icon}</span>
              {item.name}
              <FaChevronRight className="adm-nav-chev" />
            </NavLink>
          ))}
        </nav>

        <div className="adm-sidebar-ftr">
          <button className="adm-logout-btn" onClick={() => setShowLogout(true)}>
            <span className="adm-nav-ico"><FaSignOutAlt /></span>
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="adm-main">
        <header className="adm-header">
          <button className="adm-hamburger" onClick={() => setSidebarOpen(o => !o)}>
            {sidebarOpen ? <FaTimes /> : <FaBars />}
          </button>
          <div className="adm-hdr-title">
            <div className="adm-hdr-page">{getPageTitle()}</div>
            <div className="adm-hdr-sub">BOAS — Admin Panel</div>
          </div>
          <div className="adm-user-chip">
            <div className="adm-user-avatar"><FaUserCircle /></div>
            <span className="adm-user-name">{currUser?.Name || "Admin"}</span>
          </div>
        </header>

        <main className="adm-content">{children || <Outlet />}</main>

        <footer className="adm-footer">
          <div className="adm-footer-l">© 2025 <span>BOAS</span>. All rights reserved.</div>
          <div className="adm-footer-r">Admin Panel v2.0</div>
        </footer>
      </div>

      {/* Logout Modal */}
      {showLogout && (
        <div className="adm-moverlay">
          <div className="adm-mbox">
            <div className="adm-mico">🚪</div>
            <div className="adm-mtitle">Sign Out</div>
            <p className="adm-msub">Are you sure you want to sign out of your account?</p>
            <div className="adm-mbtns">
              <button className="adm-mcancel" onClick={() => setShowLogout(false)}>Cancel</button>
              <button className="adm-mconfirm" onClick={confirmLogout}>Yes, Sign Out</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
